// In-depth Git lessons (Codeazy lesson standard).

export const gitLessons = [
  {
    slug: "git-basics",
    title: "How Git Works: The Core Workflow",
    summary: "Snapshots, the three areas, commits, history and .gitignore: the mental model that makes every Git command make sense.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Every software team uses Git. It is how you save work, collaborate, review code and recover from mistakes. Developers who only memorise commands panic when something goes wrong. Developers who understand the model can fix almost anything." },

      { type: "h2", text: "The mental model: a photo album of snapshots" },
      { type: "p", text: "Git doesn’t store “changes”. It stores **snapshots**. Each commit is a photo of your entire project at a moment, plus a caption (the message), the photographer (the author) and a pointer to the previous photo (the parent). Together, the photos form a chain, which is your history. A **branch** is just a sticky note pointing at one photo." },
      { type: "code", language: "text", code: `  A ◄── B ◄── C   ← main (branch pointer)
                ▲
               HEAD (where you are now)

Each commit knows its parent. Branches are lightweight labels.` },

      { type: "h2", text: "The three areas" },
      { type: "code", language: "text", code: `Working directory  ──git add──►  Staging area  ──git commit──►  Repository
(files you edit)                 (next snapshot)                (saved history)
        ◄────────────── git restore / git checkout ──────────────` },
      { type: "table", headers: ["Area", "What it is", "Analogy"], rows: [
        ["Working directory", "Your actual files on disk", "Your messy desk"],
        ["Staging area (index)", "What will go into the next commit", "Items placed in the photo frame"],
        ["Repository (.git)", "Every commit, permanently stored", "The photo album"]
      ] },
      { type: "callout", variant: "tip", text: "The staging area lets you commit **part** of your changes. If you fixed a bug and also refactored something, stage and commit them separately for a clean history. `git add -p` lets you stage individual chunks of a file." },

      { type: "h2", text: "Everyday commands" },
      { type: "code", language: "bash", code: `git init                          # start a repo (or: git clone <url>)
git status                        # what changed? what's staged?
git diff                          # unstaged changes
git diff --staged                 # staged changes (what will be committed)

git add src/app.js                # stage a file
git add -p                        # stage chunks interactively
git commit -m "Add course search endpoint"

git log --oneline --graph --all   # visual history
git show a1b2c3d                  # one commit's details
git blame src/app.js              # who last changed each line, and in which commit` },
      { type: "code", language: "text", code: `$ git status
On branch main
Changes to be committed:
        modified:   src/routes/courses.js       ← staged

Changes not staged for commit:
        modified:   src/app.js                  ← changed, not staged

Untracked files:
        src/routes/search.js                    ← new file Git doesn't track yet` },

      { type: "h2", text: "What a commit really contains" },
      { type: "code", language: "text", code: `$ git cat-file -p HEAD
tree 9f2c1e...            ← snapshot of every file and folder
parent 4b7d0a...          ← previous commit
author Asha Rao <asha@codeazy.in> 1791200000 +0530
committer Asha Rao <asha@codeazy.in> 1791200000 +0530

Add course search endpoint` },
      { type: "p", text: "The commit ID (a SHA hash) is calculated from all of this content. Change anything, even one character of the message, and you get a different commit. This is why rewriting history creates **new** commits rather than editing old ones." },

      { type: "h2", text: "Writing good commit messages" },
      { type: "compare", badLabel: "Useless history", bad: `fix
update
changes
wip
final final 2`, goodLabel: "Searchable, meaningful history", good: `Fix double submission on enroll button

The button stayed enabled while the request
was in flight, creating duplicate enrollments.
Disable it until the request settles.

Closes #142` },
      { type: "ul", items: [
        "Subject line: about 50 characters or fewer, imperative mood (“Add”, “Fix”, “Remove”), no full stop.",
        "Body: explain **why**. The diff already shows what changed.",
        "One logical change per commit. Small commits are easier to review, revert and bisect.",
        "Many teams use **Conventional Commits**: `feat: add search`, `fix: prevent double submit`, `docs: update README`."
      ] },

      { type: "h2", text: ".gitignore" },
      { type: "code", language: "text", code: `# dependencies
node_modules/

# builds
build/
dist/

# environment and secrets
.env
.env.*
!.env.example

# OS and editor files
.DS_Store
.vscode/
*.log` },
      { type: "callout", variant: "warning", text: "`.gitignore` only affects untracked files. If `.env` was already committed, ignoring it later doesn’t remove it from history. Run `git rm --cached .env`, commit, and **rotate every secret it contained**, because anyone with the repo history can still read them." },

      { type: "h2", text: "Remotes: GitHub, GitLab and friends" },
      { type: "code", language: "bash", code: `git remote -v                       # list remotes (usually "origin")
git fetch origin                    # download new commits, don't change your files
git pull                            # fetch + merge (or rebase) into your branch
git push -u origin feature/search   # upload your branch and set upstream` },
      { type: "table", headers: ["Command", "Downloads?", "Changes your branch?"], rows: [
        ["`git fetch`", "Yes", "No. It updates `origin/main` so you can inspect it first"],
        ["`git pull`", "Yes", "Yes. It fetches, then merges or rebases into your current branch"]
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between Git and GitHub?", answer: ["Git is a distributed version control system that runs locally and tracks history. GitHub (like GitLab or Bitbucket) is a hosting platform for Git repositories that adds collaboration features: pull requests, code review, issues, CI/CD and permissions."] },
      { type: "qa", question: "What is the staging area?", answer: ["An intermediate area (the index) where you assemble the exact snapshot for your next commit. It lets you commit only some of your changes and review what will be committed with `git diff --staged`."] },
      { type: "qa", question: "What is the difference between `git fetch` and `git pull`?", answer: ["`git fetch` downloads new commits and updates remote-tracking branches such as `origin/main` without touching your working branch. `git pull` is fetch followed by a merge (or rebase, if configured) into your current branch."] },
      { type: "qa", question: "Does Git store diffs or snapshots?", answer: ["Conceptually, snapshots: each commit points to a tree representing the full project state. Unchanged files are reused by reference, and Git compresses objects into packfiles using deltas internally for storage efficiency."] },
      { type: "qa", question: "What is HEAD?", answer: ["A pointer to the commit you currently have checked out, usually indirectly through a branch name, as in “HEAD → main”. If you check out a commit directly, HEAD points to it without a branch, which is called a “detached HEAD”."] },

      { type: "exercise", title: "Split a messy change into clean commits", task: "You edited `src/app.js` to fix a bug (lines 10–14) and, in the same file, renamed some variables (lines 40–60). You also created `notes.txt`, which shouldn’t be committed. Write the commands to make two separate commits and keep `notes.txt` out of Git permanently.", language: "bash", solution: `echo "notes.txt" >> .gitignore

git add -p src/app.js        # answer "y" for the bug-fix hunk, "n" for the renames
git commit -m "Fix crash when course list is empty"

git add src/app.js .gitignore
git commit -m "Rename course variables for clarity"

git log --oneline -2`, output: `9c1e2f4 Rename course variables for clarity
3a7b8d0 Fix crash when course list is empty`, explanation: "`git add -p` stages selected hunks, so the bug fix gets its own commit. That makes it easy to review, cherry-pick or revert on its own. The rename follows as a separate commit, and `.gitignore` keeps `notes.txt` untracked for good." },

      { type: "summary", items: [
        "Git stores snapshots linked by parent pointers, and branches are lightweight labels.",
        "Changes flow from the working directory to staging (`add`) to the repository (`commit`).",
        "Use `status`, `diff`, `diff --staged` and `log --oneline --graph` constantly.",
        "Write small, focused commits with imperative subjects and a “why” in the body.",
        "Ignore secrets and builds, and rotate any secret that was ever committed."
      ] }
    ]
  },

  {
    slug: "branching-and-merging",
    title: "Branching, Merging and Pull Requests",
    summary: "Feature branches, fast-forward vs merge commits, resolving conflicts and the pull request workflow used by real teams.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Teams work on many features at once without breaking each other’s code. Branches, merges and pull requests are how that happens. Resolving merge conflicts calmly and keeping PRs reviewable are everyday skills that interviewers and teammates notice." },

      { type: "h2", text: "The mental model: parallel universes" },
      { type: "p", text: "Creating a branch opens a **parallel universe** that starts as a copy of the current one. You can experiment freely. If the experiment works, you merge the universes. If not, you delete the branch and nothing in `main` was ever touched." },
      { type: "code", language: "text", code: `            D ── E      ← feature/search
           /
  A ── B ── C ── F      ← main` },

      { type: "h2", text: "Working with branches" },
      { type: "code", language: "bash", code: `git switch -c feature/course-search   # create and switch (older: git checkout -b)
git branch                            # list local branches
git branch -a                         # include remote branches
git switch main                       # move back
git branch -d feature/course-search   # delete a merged branch
git push origin --delete feature/course-search   # delete it on the remote` },
      { type: "table", headers: ["Convention", "Example"], rows: [
        ["Feature", "`feature/live-class-form`"],
        ["Bug fix", "`fix/double-enroll`"],
        ["Chore or refactor", "`chore/upgrade-react-19`"],
        ["With a ticket ID", "`feat/CZ-142-course-search`"]
      ] },

      { type: "h2", text: "Fast-forward vs merge commit" },
      { type: "code", language: "text", code: `Fast-forward (main didn't move):

  A ── B ── C           main
             \\
              D ── E    feature
  → git merge feature just moves the main pointer to E:  A ── B ── C ── D ── E

Three-way merge (both moved):

  A ── B ── C ── F             main
             \\
              D ── E           feature
  → creates merge commit M with two parents:

  A ── B ── C ── F ── M        main
             \\       /
              D ── E` },
      { type: "code", language: "bash", code: `git switch main
git pull                          # get the latest main first
git merge feature/course-search   # fast-forward if possible, otherwise a merge commit
git merge --no-ff feature/x       # always create a merge commit (keeps the feature grouped)` },

      { type: "h2", text: "Resolving merge conflicts" },
      { type: "p", text: "A conflict happens when both branches changed **the same lines** (or one deleted a file the other edited). Git can’t guess which version is right, so it stops and asks you:" },
      { type: "code", language: "text", code: `$ git merge feature/pricing
Auto-merging src/data/courses.js
CONFLICT (content): Merge conflict in src/data/courses.js
Automatic merge failed; fix conflicts and then commit the result.` },
      { type: "code", language: "javascript", code: `export const courses = [
<<<<<<< HEAD
  { id: "dsa", title: "DSA", duration: "4 months" },
=======
  { id: "dsa", title: "Data Structures & Algorithms", price: 4999 },
>>>>>>> feature/pricing
];` },
      { type: "ol", items: [
        "Run `git status` to see which files are conflicted.",
        "Open each file. Between `<<<<<<< HEAD` and `=======` is your current branch, and between `=======` and `>>>>>>>` is the incoming branch.",
        "Edit the code into the correct final version. Often that **combines both** changes, not just one side.",
        "Delete the conflict markers, then run the app and tests.",
        "Run `git add` on the file, then `git commit`, or `git merge --continue`.",
        "Changed your mind? `git merge --abort` returns to the state before the merge."
      ] },
      { type: "code", language: "javascript", code: `// Resolved: keeps the new title AND both fields
export const courses = [
  { id: "dsa", title: "Data Structures & Algorithms", duration: "4 months", price: 4999 },
];` },
      { type: "callout", variant: "tip", text: "VS Code shows “Accept Current / Incoming / Both” buttons above conflicts, and its 3-way merge editor makes complex conflicts much easier. Pull `main` into your branch often, because small, frequent merges mean small, easy conflicts." },

      { type: "h2", text: "The pull request workflow" },
      { type: "ol", items: [
        "Create a branch from an up-to-date `main`.",
        "Make small, focused commits, and push the branch.",
        "Open a **pull request** (PR) with a clear title and description: what changed, why, how to test it, and screenshots for UI changes.",
        "CI runs automatically: tests, lint and build. Fix anything red.",
        "Teammates review and comment. You push follow-up commits to the same branch.",
        "Once approved and green, merge (squash, merge commit or rebase) and delete the branch."
      ] },
      { type: "code", language: "text", code: `## What
Adds a Live Classes page with cohort cards and a "Request a seat" form.

## Why
Students asked for live, mentor-led batches (see #131).

## How to test
1. npm run dev, open /live-classes
2. Click "Request to join" on a cohort → form is prefilled
3. Submit → request appears in /admin-dashboard

## Screenshots
[desktop] [mobile]` },
      { type: "table", headers: ["Merge strategy", "Result on main", "Good for"], rows: [
        ["Merge commit", "Every branch commit plus a merge commit", "Preserving full history"],
        ["Squash and merge", "One commit per PR", "Clean, linear history, and easy to revert a feature"],
        ["Rebase and merge", "The branch commits replayed linearly", "Linear history while keeping individual commits"]
      ] },

      { type: "h2", text: "Giving and receiving reviews" },
      { type: "ul", items: [
        "Keep PRs small (under about 400 changed lines). Large PRs get rubber-stamped instead of reviewed.",
        "As a reviewer, check correctness, edge cases, naming and tests. Leave style issues to linters and formatters.",
        "Phrase feedback as questions or suggestions: “What happens if `courses` is empty here?”",
        "As an author, review your own diff first, and respond to every comment (fix it, or explain why not)."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is a merge conflict and how do you resolve it?", answer: ["It happens when two branches change the same part of a file and Git can’t combine them automatically. Open the conflicted files, edit the sections between the markers into the correct final code (often combining both sides), remove the markers, test, `git add` the files and complete the merge. `git merge --abort` cancels it."] },
      { type: "qa", question: "What is the difference between a fast-forward merge and a three-way merge?", answer: ["A fast-forward happens when the target branch hasn’t moved since the feature branched off. Git just moves the pointer forward, with no new commit. A three-way merge happens when both branches have new commits. Git combines them using their common ancestor and creates a merge commit with two parents."] },
      { type: "qa", question: "What makes a good pull request?", answer: ["It is small and focused on one change, has a descriptive title, and explains what, why and how to test it, with screenshots for UI changes. It has passing CI and tests, links to its ticket, and its author has reviewed it first."] },
      { type: "qa", question: "What is the difference between squash-and-merge and a merge commit?", answer: ["Squash combines all of a PR’s commits into one new commit on main, which keeps history clean and makes reverting a feature easy, but loses the individual commits. A merge commit keeps every commit plus a merge commit, which preserves detail at the cost of a noisier history."] },

      { type: "exercise", title: "Simulate and resolve a conflict", task: "Write the commands to: create a repo with `README.md` containing \"Codeazy\"; create branch `a` that changes the line to \"Codeazy: Learn\"; go back to main and change it to \"Codeazy Academy\"; merge `a` into main; and resolve the conflict to \"Codeazy Academy: Learn\".", language: "bash", solution: `git init demo && cd demo
echo "Codeazy" > README.md
git add README.md && git commit -m "Add README"

git switch -c a
echo "Codeazy: Learn" > README.md
git commit -am "Add tagline"

git switch main
echo "Codeazy Academy" > README.md
git commit -am "Rename to Academy"

git merge a                      # CONFLICT in README.md
echo "Codeazy Academy: Learn" > README.md    # resolve: combine both intents
git add README.md
git commit -m "Merge branch 'a'"

git log --oneline --graph`, output: `*   e41b7a2 Merge branch 'a'
|\\
| * 7c0d913 Add tagline
* | 2f9a6b1 Rename to Academy
|/
* 1d2e3f4 Add README`, explanation: "Both branches edited the same line after the common ancestor, so Git can’t choose. The right resolution keeps **both** intents, the new name and the tagline. That is usually the case in real conflicts too." },

      { type: "summary", items: [
        "Branches are cheap parallel universes, so create one per feature or fix.",
        "Merges are either fast-forwards or three-way merges that create a merge commit.",
        "Resolve conflicts by editing to the correct combined result, removing markers and testing.",
        "Use small, well-described PRs with green CI for team collaboration.",
        "Pick a merge strategy (merge, squash or rebase) as a team and use it consistently."
      ] }
    ]
  },

  {
    slug: "undoing-and-rewriting",
    title: "Undoing Mistakes: restore, reset, revert, stash and rebase",
    summary: "Fix almost any Git mistake safely, and know which commands rewrite history and when that is dangerous.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Everyone commits to the wrong branch, includes a secret by accident or wants to tidy their commits before a PR. Knowing the right undo command, and which ones are dangerous on shared branches, saves hours and avoids wrecking teammates’ work." },

      { type: "h2", text: "The mental model: private notebook vs published book" },
      { type: "p", text: "Commits that exist only on your machine are a **private notebook**: you can tear out and rewrite pages freely (`reset`, `rebase`, `commit --amend`). Once commits are pushed to a shared branch, they are a **published book**. Others have copies, so you add a correction page (`revert`) instead of rewriting history." },

      { type: "h2", text: "Which undo do I need?" },
      { type: "table", headers: ["Situation", "Command"], rows: [
        ["Discard uncommitted changes to a file", "`git restore src/app.js`"],
        ["Unstage a file (keep the changes)", "`git restore --staged src/app.js`"],
        ["Fix the last commit’s message or add a forgotten file", "`git commit --amend`"],
        ["Undo the last commit but keep the changes", "`git reset --soft HEAD~1`"],
        ["Throw away local commits completely", "`git reset --hard <commit>` (destructive)"],
        ["Undo a commit that is already pushed or shared", "`git revert <commit>`"],
        ["Pause work to switch branches", "`git stash` / `git stash pop`"],
        ["Recover “lost” commits after a bad reset", "`git reflog`"]
      ] },

      { type: "h2", text: "git reset: moving the branch pointer" },
      { type: "code", language: "text", code: `Before:  A ── B ── C   ← main (HEAD)
git reset <mode> B
After:   A ── B        ← main (HEAD)      C is no longer on the branch` },
      { type: "table", headers: ["Mode", "Branch pointer", "Staging area", "Working files", "Use"], rows: [
        ["`--soft`", "Moves to B", "Keeps C’s changes staged", "Unchanged", "Re-do the commit differently"],
        ["`--mixed` (default)", "Moves to B", "Unstages C’s changes", "Unchanged", "Re-pick what to stage"],
        ["`--hard`", "Moves to B", "Reset", "C’s changes are **deleted**", "Throw away work (careful!)"]
      ] },
      { type: "code", language: "bash", code: `# Committed to main by mistake? Move the commit to a new branch:
git branch feature/oops         # new branch points at the current commit
git reset --hard HEAD~1         # move main back one commit
git switch feature/oops         # the work is safe here` },

      { type: "h2", text: "git revert: the safe undo for shared history" },
      { type: "code", language: "bash", code: `git revert a1b2c3d              # creates a NEW commit that reverses a1b2c3d
git revert -m 1 <merge-commit>  # revert a merge, keeping parent 1 (main)` },
      { type: "code", language: "text", code: `A ── B ── C ── C'        C' = "Revert C" (history preserved, safe to push)` },
      { type: "callout", variant: "warning", text: "Never `reset --hard` and `push --force` on a shared branch such as `main`. Teammates’ histories will diverge and their work can be lost. If you must force-push your own feature branch, use `git push --force-with-lease`, which refuses if someone else pushed in the meantime." },

      { type: "h2", text: "git stash: a temporary shelf" },
      { type: "code", language: "bash", code: `git stash push -m "half-done navbar"   # shelve uncommitted changes
git switch main                         # urgent hotfix...
git switch feature/navbar
git stash list                          # stash@{0}: On feature/navbar: half-done navbar
git stash pop                           # re-apply and remove from the shelf
git stash push -u                       # also include untracked files` },

      { type: "h2", text: "git rebase: replaying commits" },
      { type: "p", text: "Rebasing moves your branch so it starts from the latest `main`, by replaying your commits on top. The result is a straight line of history with no merge commit:" },
      { type: "code", language: "text", code: `Before:                         After git rebase main:
  A ── B ── F        main         A ── B ── F                 main
        \\                                    \\
         D ── E      feature                   D' ── E'      feature
                                  (D' and E' are NEW commits with new IDs)` },
      { type: "code", language: "bash", code: `git switch feature/search
git fetch origin
git rebase origin/main
# conflicts? fix them, then:
git add <file> && git rebase --continue
# or give up and go back:
git rebase --abort` },
      { type: "table", headers: ["", "Merge", "Rebase"], rows: [
        ["History", "True history, with merge commits", "Linear and tidy"],
        ["Rewrites commits", "No", "Yes (new IDs)"],
        ["Safe on shared branches", "Yes", "Only for commits nobody else has based work on"],
        ["Conflicts", "Resolved once", "Possibly once per replayed commit"]
      ] },
      { type: "callout", variant: "note", title: "The golden rule of rebasing", text: "Don’t rebase commits that other people have already pulled. Rebasing your own local feature branch onto `main` before opening or updating a PR is fine and common." },

      { type: "h3", text: "Interactive rebase: tidy up before a PR" },
      { type: "code", language: "text", code: `$ git rebase -i HEAD~4

pick   3a1f2c0 Add course search endpoint
squash 9b2e1d4 fix typo
squash 4c7d8e2 oops forgot file
reword 1e5f6a3 add tests

# pick = keep, squash = merge into previous, reword = edit message,
# drop = delete, edit = stop to amend` },

      { type: "h2", text: "git reflog: the safety net" },
      { type: "p", text: "Git records every position HEAD has been in, even after a `reset --hard` or a messy rebase. Commits stay recoverable for a while (90 days by default for reachable entries):" },
      { type: "code", language: "text", code: `$ git reflog
e41b7a2 HEAD@{0}: reset: moving to HEAD~3
9c8d7e6 HEAD@{1}: commit: Add payment webhook       ← the "lost" work
...

$ git reset --hard 9c8d7e6     # or: git branch rescue 9c8d7e6` },

      { type: "h2", text: "Other lifesavers" },
      { type: "code", language: "bash", code: `git cherry-pick a1b2c3d     # copy one commit onto the current branch (e.g. a hotfix)
git bisect start            # binary-search history for the commit that broke something
git bisect bad              # current commit is broken
git bisect good v1.4.0      # this tag was fine → Git checks out the midpoint; repeat
git bisect reset` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between `git reset` and `git revert`?", answer: ["`reset` moves the branch pointer backwards, removing commits from the branch and optionally discarding changes, so it rewrites history and is only safe for unpushed commits. `revert` creates a new commit that undoes an earlier commit’s changes, preserving history, so it is safe for shared branches."] },
      { type: "qa", question: "Explain `--soft`, `--mixed` and `--hard`.", answer: ["All three move the branch pointer. `--soft` keeps the undone changes staged. `--mixed` (the default) keeps them in the working directory but unstaged. `--hard` discards them from both the index and the working directory."] },
      { type: "qa", question: "What is the difference between merge and rebase? When would you use each?", answer: ["Merge combines histories with a merge commit and never rewrites existing commits. Rebase replays your commits on top of another base, producing linear history with new commit IDs. Rebase your private feature branch to stay current with main and tidy commits. Use merge (or a squash merge through a PR) to integrate into shared branches."] },
      { type: "qa", question: "How would you recover a commit after `git reset --hard`?", answer: ["Use `git reflog` to find the commit’s hash in HEAD’s history, then `git reset --hard <hash>` or create a branch at it with `git branch rescue <hash>`."] },
      { type: "qa", question: "You accidentally committed an API key and pushed it. What do you do?", answer: ["First, revoke and rotate the key immediately, because it must be considered compromised. Then remove it from the code, add the file to `.gitignore`, and if needed purge it from history with `git filter-repo` or BFG and force-push in coordination with the team. Enable secret scanning to prevent a repeat."] },
      { type: "qa", question: "What does `git cherry-pick` do?", answer: ["It applies the changes from a specific commit onto your current branch as a new commit. It is commonly used to bring a hotfix from main into a release branch."] },

      { type: "exercise", title: "Clean up before a PR", task: "Your feature branch has 4 commits: \"Add search API\", \"wip\", \"fix lint\" and \"Add search tests\", and `main` has moved on since you branched. Write the commands to update your branch onto the latest main, combine \"wip\" and \"fix lint\" into \"Add search API\", and push the cleaned branch safely.", language: "bash", solution: `git switch feature/search
git fetch origin
git rebase -i origin/main

# In the editor:
#   pick   1111111 Add search API
#   fixup  2222222 wip            (fixup = squash, discarding this message)
#   fixup  3333333 fix lint
#   pick   4444444 Add search tests

# Resolve any conflicts → git add <file> → git rebase --continue
npm test                               # make sure everything still works
git push --force-with-lease            # safe force-push of your own branch`, explanation: "Rebasing onto `origin/main` replays your work on the latest code, and the interactive todo list lets you fold the noise commits away. `--force-with-lease` is needed because the commit IDs changed, but it refuses to overwrite the remote branch if someone else pushed to it." },

      { type: "summary", items: [
        "Local commits are a private notebook you can rewrite, and pushed commits are a published book you should only revert.",
        "`restore` handles files, `reset` moves the branch (`soft`, `mixed` or `hard`), and `revert` adds an inverse commit.",
        "`stash` shelves work in progress, and `cherry-pick` copies single commits.",
        "Rebase gives a linear history, but never rebase commits others have pulled. Use `--force-with-lease`.",
        "`reflog` can rescue almost anything, and `bisect` finds the commit that broke something."
      ] }
    ]
  },

  {
    slug: "team-workflows",
    title: "Team Workflows, Releases and Good Habits",
    summary: "Trunk-based vs GitFlow, protected branches, tags and releases, CI hooks and monorepo tips used in professional teams.",
    readTime: 11,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Knowing Git commands isn’t the same as working well in a team. Branching strategies, release tagging, protected branches and automation decide how fast and safely a team ships. Interviewers often ask “What Git workflow did your team use, and why?”" },

      { type: "h2", text: "The mental model: traffic rules" },
      { type: "p", text: "A workflow is a set of **traffic rules** for code. Everyone can drive (commit), but the rules decide which roads lead to production, who checks the vehicles (reviews and CI) and how accidents get cleared (hotfixes and reverts). Simple, consistent rules keep traffic moving." },

      { type: "h2", text: "Common branching strategies" },
      { type: "table", headers: ["", "Trunk-based development", "GitHub Flow", "GitFlow"], rows: [
        ["Main branches", "`main` only", "`main` only", "`main` + `develop`"],
        ["Feature branches", "Very short-lived (hours to 1–2 days)", "Short-lived, merged by PR", "Longer, off `develop`"],
        ["Releases", "Continuous, from main (with feature flags)", "Deploy main after merge", "`release/*` branches, then tag"],
        ["Hotfixes", "Fix forward on main", "PR to main", "`hotfix/*` off main, merged back to both"],
        ["Best for", "High-velocity teams with strong CI", "Most web apps and startups", "Versioned products with scheduled releases"]
      ] },
      { type: "callout", variant: "tip", text: "Most modern web teams use GitHub Flow or trunk-based development. GitFlow adds overhead that suits mobile apps or shipped software with versioned releases better than continuously deployed websites." },

      { type: "h2", text: "Feature flags: merging unfinished work safely" },
      { type: "code", language: "javascript", code: `// Code is merged and deployed, but hidden until the flag is on
if (flags.isEnabled("practice-section", user)) {
  routes.push({ path: "/practice", element: <Practice /> });
}` },
      { type: "p", text: "Flags decouple **deploying** from **releasing**. They allow small, frequent merges into main, gradual rollouts (say, 5% of users) and instant rollback without a redeploy." },

      { type: "h2", text: "Protecting main" },
      { type: "ul", items: [
        "Require pull requests, so nobody pushes directly to main.",
        "Require at least one approving review (CODEOWNERS can require specific teams for specific folders).",
        "Require status checks to pass: tests, lint, build and security scans.",
        "Require branches to be up to date before merging.",
        "Block force-pushes and deletion of main."
      ] },
      { type: "code", language: "text", code: `# .github/CODEOWNERS
/server/            @codeazy/backend-team
/src/pages/Learn/   @codeazy/content-team
*.tf                @codeazy/platform-team` },

      { type: "h2", text: "Tags and semantic versioning" },
      { type: "code", language: "bash", code: `git tag -a v1.4.0 -m "Live classes and blog"   # annotated tag
git push origin v1.4.0
git tag --list "v1.*"
git checkout v1.3.2                              # inspect an old release` },
      { type: "table", headers: ["Version part", "Bump when", "Example"], rows: [
        ["MAJOR", "Breaking changes", "1.4.0 → **2**.0.0"],
        ["MINOR", "New backwards-compatible features", "1.4.0 → 1.**5**.0"],
        ["PATCH", "Backwards-compatible bug fixes", "1.4.0 → 1.4.**1**"]
      ] },

      { type: "h2", text: "Automating with hooks and CI" },
      { type: "code", language: "json", code: `// package.json: run checks before every commit with husky + lint-staged
{
  "lint-staged": {
    "*.{js,jsx}": ["eslint --fix", "prettier --write"],
    "*.css": ["prettier --write"]
  }
}` },
      { type: "code", language: "yaml", code: `# .github/workflows/ci.yml
name: CI
on: [pull_request]
jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with: { node-version: 20, cache: npm }
      - run: npm ci
      - run: npm run lint
      - run: npm test
      - run: npm run build` },

      { type: "h2", text: "Good habits checklist" },
      { type: "ul", items: [
        "Pull or rebase from main at least daily on long-running branches.",
        "Commit early and often locally, then tidy up before opening a PR.",
        "Never commit secrets, build output or `node_modules`.",
        "Keep generated lockfiles committed (`package-lock.json`).",
        "Use `.gitattributes` to normalise line endings across Windows, macOS and Linux.",
        "Write the PR description for a reviewer who has no context.",
        "Delete merged branches to keep the repository tidy."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Which branching strategy has your team used, and why?", answer: ["A strong answer names a strategy and justifies it. For example: “GitHub Flow: short-lived feature branches merged to main through PRs with required reviews and CI, then automatic deploys to staging and a manual promotion to production. It suited our web app because we deployed several times a day and didn’t need parallel release versions.”"] },
      { type: "qa", question: "What is trunk-based development?", answer: ["A workflow where everyone integrates small changes into a single main branch (the trunk) very frequently, at least daily, using short-lived branches or direct commits, with incomplete features hidden behind flags. It minimises merge pain and enables continuous delivery, but it needs strong automated testing."] },
      { type: "qa", question: "What are Git tags used for?", answer: ["Tags mark specific commits permanently, usually releases such as `v1.4.0`. Annotated tags store a message, author and date. They are used for release notes, deployments and checking out exactly what shipped."] },
      { type: "qa", question: "How do you prevent broken code from reaching main?", answer: ["Protect the branch: require PRs, approving reviews, passing CI status checks and up-to-date branches, and block force-pushes. Add pre-commit hooks for fast local checks, and use feature flags so incomplete work can merge safely."] },

      { type: "exercise", title: "Design a workflow for Codeazy", task: "Codeazy has 4 developers, deploys the website several times a week, and wants to build the new Practice section over 6 weeks without blocking other releases. Propose a branching strategy, branch protection rules and a release process in a few bullet points.", language: "text", solution: `Strategy: GitHub Flow (short-lived branches → PR → main)

Branches
- feature/<ticket>-<short-name>, kept small, merged within 1–3 days
- The Practice section is built in many small PRs behind a "practice-section" feature flag

Protection on main
- PR required, 1 approval (CODEOWNERS for /server and /src/pages/Learn)
- CI must pass: lint, tests, build
- Branch must be up to date. No force-push or deletion

Releases
- Every merge to main deploys to staging automatically
- Promote to production by tagging vX.Y.Z (semantic versioning)
- Hotfix: a fix/<name> PR to main, tagged as a patch release
- Turn on the Practice flag for 10% of users → 100% once stable`, explanation: "A 4-person team shipping weekly doesn’t need GitFlow’s overhead. Feature flags let six weeks of Practice work merge continuously instead of living in a long branch that would become a painful merge. Protection and CI keep main always deployable." },

      { type: "summary", items: [
        "Choose a workflow that fits your release style: trunk-based, GitHub Flow or GitFlow.",
        "Feature flags separate deploying from releasing, so you can merge small changes often.",
        "Protect main with required PRs, reviews, CI checks and CODEOWNERS.",
        "Tag releases with semantic versions.",
        "Automate quality with pre-commit hooks and CI on every PR."
      ] }
    ]
  }
];
